import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { getAboutPageData } from '../services/api';
import { generateAboutOrganizationSchema, generateBreadcrumbSchema, generateWebPageSchema } from '../utils/schemaGenerator';

const About = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cleanHTML = (html) => {
    return (html || "")
      .replace(/<p>(&nbsp;|\s)*<\/p>/g, "")  // remove empty <p>
      .replace(/&nbsp;/g, " ")               // replace nbsp
      .trim();
  };
  useEffect(() => {
    const fetch = async () => {
      const res = await getAboutPageData();
      setData(res);
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-stone-50 py-10 flex justify-center items-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-saffron-600"></div></div>;
  }

  if (!data) return null;

  const currentUrl = typeof window !== 'undefined' ? window.location.href : null;
  const orgSchema = generateAboutOrganizationSchema(data, currentUrl);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'About Us', url: '/about' }
  ]);

  const webPageSchema = generateWebPageSchema('AboutPage', {
    name: data.metaTitle || 'About Us - Nathnagri Bareilly',
    description: data.metaDescription || 'Information about temples and spiritual heritage of Bareilly'
  }, currentUrl || 'https://nathnagribareilly.com/about');

  const rawSchemas = [orgSchema, breadcrumbSchema, webPageSchema].filter(Boolean);

  return (
    <div className="min-h-screen bg-stone-50 py-10 md:py-16">
      <SEO
        title={data.metaTitle || "हमारे बारे में (About Us)"}
        description={data.metaDescription}
        keywords="about us, nathnagri history, bareilly shiva temples, bareilly mandir list"
        url={currentUrl || 'https://nathnagribareilly.com/about'}
        schema={rawSchemas}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* HERO SECTION */}
        <div className={`mb-10 relative ${data.heroImage ? 'h-64 md:h-80 shadow-lg' : ''}`}>
          {data.heroImage && <img src={data.heroImage} alt="Hero Background" className="w-full h-full object-cover absolute inset-0 z-0" />}
          {data.heroImage && <div className="absolute inset-0 bg-stone-900/60 z-10"></div>}

          <div className={`relative z-20 ${data.heroImage ? 'h-full flex flex-col justify-center px-8 md:px-12 text-white' : ''}`}>
            <h1 className={`text-4xl md:text-5xl font-extrabold mb-4 tracking-tight ${data.heroImage ? 'text-white' : 'text-stone-900'}`}>
              {data.heroHeading || data.pageTitle}
            </h1>
            {data.heroSubheading && (
              <p className={`text-lg md:text-xl max-w-2xl ${data.heroImage ? 'text-stone-200' : 'text-stone-600'}`}>
                {data.heroSubheading}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content (Left) */}
          <div className="flex-1 space-y-8">

            {/* MAIN CONTENT BLOCK */}
            {data.mainContent && (
              <>
                <div className="bg-white p-8 rounded-2xl border border-stone-100 shadow-sm prose prose-lg prose-stone max-w-none [&>p]:mb-6 [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mb-4 [&>h3]:mt-8 [&>a]:text-saffron-600 [&>a]:font-semibold hover:[&>a]:text-saffron-800"
                  dangerouslySetInnerHTML={{ __html: cleanHTML(data.mainContent) }} />

                {/* Embedded Ad inside content body */}
                <div className="my-8">
                  <AdBanner placement="in-content" pageName="about" />
                </div>
              </>
            )}

            {/* MISSION & VISION */}
            {(data.missionTitle || data.visionTitle) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {data.missionTitle && (
                  <div className="bg-white p-8 rounded-2xl border border-stone-100 shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="text-2xl font-bold text-saffron-600 mb-4">{data.missionTitle}</h3>
                    <div className="prose prose-stone [&>a]:text-saffron-600 [&>a]:font-semibold" dangerouslySetInnerHTML={{ __html: cleanHTML(data.missionDesc || '') }} />
                  </div>
                )}
                {data.visionTitle && (
                  <div className="bg-white p-8 rounded-2xl border border-stone-100 shadow-sm hover:shadow-md transition-shadow">
                    <h3 className="text-2xl font-bold text-saffron-600 mb-4">{data.visionTitle}</h3>
                    <div className="prose prose-stone [&>a]:text-saffron-600 [&>a]:font-semibold" dangerouslySetInnerHTML={{ __html: cleanHTML(data.visionDesc || '') }} />
                  </div>
                )}
              </div>
            )}

            {/* OPTIONAL BLOCKS */}
            {data.optionalSections && data.optionalSections.length > 0 && data.optionalSections.map((sec, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl border border-stone-100 shadow-sm">
                {sec.title && <h3 className="text-2xl font-bold text-stone-800 mb-4">{sec.title}</h3>}
                <div className="prose prose-lg prose-stone max-w-none [&>a]:text-saffron-600 [&>a]:font-semibold" dangerouslySetInnerHTML={{ __html: cleanHTML(sec.content || '') }} />
              </div>
            ))}

            {/* TEAM MEMBERS */}
            {data.teamMembers && data.teamMembers.length > 0 && (
              <div className="pt-8 mb-4">
                <h3 className="text-3xl font-extrabold text-stone-900 mb-8 text-center bg-gradient-to-r from-stone-800 to-stone-600 bg-clip-text text-transparent">
                  हमारी टीम (Our Team)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {data.teamMembers.map((member, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm flex flex-col sm:flex-row gap-6 items-center sm:items-start text-center sm:text-left hover:shadow-lg hover:-translate-y-1 transition-all">
                      {member.photo ? (
                        <img src={member.photo} alt={member.name} className="w-24 h-24 rounded-full object-cover shadow-md flex-shrink-0 border-2 border-white" />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 border-2 border-stone-200">
                          <span className="text-stone-400 font-bold text-2xl uppercase">{member.name.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <h4 className="text-xl font-bold text-stone-900">{member.name}</h4>
                        <p className="text-saffron-600 font-semibold mb-3 text-sm tracking-wide">{member.role}</p>
                        <div className="text-stone-600 text-sm prose prose-sm [&>p]:leading-relaxed" dangerouslySetInnerHTML={{ __html: cleanHTML(member.bio || '') }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CALL TO ACTION */}
            {data.ctaText && (
              <div className="bg-stone-900 rounded-3xl p-10 md:p-14 text-center mt-12 relative overflow-hidden shadow-xl border-t-4 border-saffron-500">
                <div className="absolute top-0 right-0 w-64 h-64 bg-saffron-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-30 -mr-20 -mt-20"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-saffron-500 rounded-full mix-blend-multiply filter blur-[80px] opacity-30 -ml-20 -mb-20"></div>

                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-8 relative z-10 leading-tight max-w-2xl mx-auto">
                  {data.ctaText}
                </h2>
                {data.ctaBtnLabel && data.ctaBtnLink && (
                  <Link to={data.ctaBtnLink} className="inline-block bg-saffron-500 hover:bg-saffron-400 text-stone-900 hover:text-stone-950 font-bold py-3.5 px-10 rounded-xl transition-all shadow-lg hover:shadow-saffron-500/40 hover:-translate-y-1 relative z-10 text-lg">
                    {data.ctaBtnLabel}
                  </Link>
                )}
              </div>
            )}

          </div>

          {/* Sidebar (Right) */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <AdBanner placement="sidebar" pageName="about" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
